import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, Loader2, X } from "lucide-react";
import { CaptionStyleGrid } from "@/components/reelflo/CaptionStyleGrid";
import { MusicTrackList } from "@/components/reelflo/MusicTrackList";

/**
 * First-run template wizard — ONE bold decision per screen, with conditional
 * substeps, ending on a MANDATORY naming step. It never uploads and never
 * mutates the live workspace: on finish it hands a complete 8-setting snapshot
 * + a name (or an overwrite target) back to the parent, which performs the
 * save/overwrite and default assignment. Back / Close / Escape save NOTHING.
 */
const PHASES = ["broll", "broll_pct", "silence", "pace", "caption", "music", "track", "name"];

const num = (v, d) => (typeof v === "number" && Number.isFinite(v) ? v : d);

// Mario-confirmed hidden default: every NEW wizard-created template stores a
// deliberately subtle 9% music volume (integer 9 on the 0-100 scale). The volume
// STEP is removed from the wizard entirely — creators never see or set it. Edit
// mode preserves the existing template's stored volume unchanged.
const NEW_TEMPLATE_MUSIC_VOLUME = 9;

export const FirstRunTemplateWizard = ({
    open,
    mode = "create",
    initialSettings = null,
    initialName = "",
    musicTracks = [],
    musicLoading = false,
    musicError = false,
    musicLoadedOnce = false,
    onRequestMusicLoad,
    existingPresets = [],
    captionStyles = [],
    customCaptionSelection = null,
    customCaptionAvailable = false,
    onCancel,
    onFinish,
    onRetryDefault,
}) => {
    const s0 = initialSettings || {};
    const [brollOn, setBrollOn] = useState(s0.broll_on ?? true);
    const [brollPct, setBrollPct] = useState(num(s0.broll_pct, 50));
    const [removeSilence, setRemoveSilence] = useState(s0.remove_silence ?? true);
    const [silencePace, setSilencePace] = useState(s0.silence_pace || "natural");
    const [caption, setCaption] = useState(s0.caption_template || (captionStyles[0]?.templateName));
    const [musicEnabled, setMusicEnabled] = useState(s0.music_enabled ?? false);
    const [trackId, setTrackId] = useState(s0.music_selected_id ?? null);
    const [volume, setVolume] = useState(
        mode === "edit" ? num(s0.music_volume, NEW_TEMPLATE_MUSIC_VOLUME) : NEW_TEMPLATE_MUSIC_VOLUME,
    );
    const [name, setName] = useState(initialName || "");
    const [phase, setPhase] = useState("broll");
    const [saving, setSaving] = useState(false);
    const [nameErr, setNameErr] = useState("");
    const [retryPresetId, setRetryPresetId] = useState(null);

    // Reset whenever the wizard (re)opens so Edit prefills and Create is fresh.
    useEffect(() => {
        if (!open) return;
        const s = initialSettings || {};
        setBrollOn(s.broll_on ?? true);
        setBrollPct(num(s.broll_pct, 50));
        setRemoveSilence(s.remove_silence ?? true);
        setSilencePace(s.silence_pace || "natural");
        setCaption(s.caption_template || captionStyles[0]?.templateName);
        setMusicEnabled(s.music_enabled ?? false);
        setTrackId(s.music_selected_id ?? null);
        setVolume(mode === "edit" ? num(s.music_volume, NEW_TEMPLATE_MUSIC_VOLUME) : NEW_TEMPLATE_MUSIC_VOLUME);
        setName(initialName || "");
        setPhase("broll");
        setNameErr("");
        setRetryPresetId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const panelRef = useRef(null);
    const openerRef = useRef(null);

    // Initial focus when the wizard opens; focus returns to the opener when it
    // closes (any close path — Back on first step, Close, Escape, or finish).
    useEffect(() => {
        if (!open) return undefined;
        openerRef.current = document.activeElement;
        if (panelRef.current) panelRef.current.focus();
        return () => {
            const opener = openerRef.current;
            if (opener && typeof opener.focus === "function") opener.focus();
        };
    }, [open]);

    // Step transitions unmount the clicked option, dropping focus to <body>;
    // pull focus back inside the panel so Tab containment keeps holding.
    useEffect(() => {
        if (!open) return;
        const root = panelRef.current;
        if (root && !root.contains(document.activeElement)) root.focus();
    }, [open, phase]);

    const visible = (p) => {
        if (p === "broll_pct") return brollOn;
        if (p === "pace") return removeSilence;
        if (p === "track") return musicEnabled;
        return true;
    };
    const order = useMemo(() => PHASES.filter(visible),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [brollOn, removeSilence, musicEnabled]);

    // When the music substep is reached with no catalog yet, ask the parent to
    // load it (fail-closed self-heal). Never blocks other steps.
    useEffect(() => {
        if (!open) return;
        if (phase === "track" && !musicLoadedOnce && !musicLoading && !musicError) {
            onRequestMusicLoad?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, phase]);

    if (!open) return null;

    // Tab / Shift+Tab containment while open. Pure presentation: the step
    // machine, save ordering, close eligibility and callbacks are untouched.
    const containFocus = (e) => {
        if (e.key !== "Tab") return;
        const root = panelRef.current;
        if (!root) return;
        const focusables = Array.from(
            root.querySelectorAll(
                'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement;
        if (e.shiftKey) {
            if (current === first || !root.contains(current)) {
                e.preventDefault();
                last.focus();
            }
        } else if (current === last || !root.contains(current)) {
            e.preventDefault();
            first.focus();
        }
    };

    const advanceFrom = (p, overrides = {}) => {
        const st = { brollOn, removeSilence, musicEnabled, ...overrides };
        const vis = (q) => {
            if (q === "broll_pct") return st.brollOn;
            if (q === "pace") return st.removeSilence;
            if (q === "track") return st.musicEnabled;
            return true;
        };
        const later = PHASES.slice(PHASES.indexOf(p) + 1).filter(vis);
        setPhase(later[0] || "name");
    };
    const back = () => {
        const earlier = order.filter((p) => PHASES.indexOf(p) < PHASES.indexOf(phase));
        if (earlier.length === 0) return onCancel?.();
        setPhase(earlier[earlier.length - 1]);
    };

    const nameLc = name.trim().toLowerCase();
    const collision = existingPresets.find((p) => (p.name || "").toLowerCase() === nameLc && nameLc);

    // Caption tiles for the shared visual grid = the eight built-ins plus the
    // operator-gated custom tile when available.
    const captionOptions = [
        ...captionStyles,
        ...(customCaptionAvailable && customCaptionSelection
            ? [{ id: "custom", label: "Your custom theme", templateName: customCaptionSelection, variant: "custom" }]
            : []),
    ];

    const doFinish = async (overwriteId) => {
        const settings = {
            broll_on: !!brollOn,
            broll_pct: brollPct,
            remove_silence: !!removeSilence,
            silence_pace: silencePace,
            caption_template: caption,
            music_enabled: !!musicEnabled,
            music_selected_id: musicEnabled ? trackId : null,
            music_volume: volume,
        };
        setSaving(true);
        try {
            const res = await onFinish?.({ settings, name: name.trim(), overwriteId: overwriteId || null });
            if (res && res.ok === false) {
                if (res.needDefaultRetry) {
                    setRetryPresetId(res.presetId || null);
                } else if (res.nameConflict) {
                    setNameErr("A template with that name already exists. Pick a different name, or overwrite it.");
                }
            }
        } finally {
            setSaving(false);
        }
    };

    const submitName = () => {
        setNameErr("");
        if (!name.trim()) { setNameErr("Enter a template name."); return; }
        if (collision) {
            // Same name => overwrite only; "Save as new" is not allowed.
            setNameErr("");
            return; // user must press the explicit Overwrite button
        }
        doFinish(null);
    };

    const Opt = ({ tid, active, onClick, children }) => (
        <button
            type="button"
            data-testid={tid}
            onClick={onClick}
            className={`min-h-11 w-full rounded-lg border px-4 py-3 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 ${
                active
                    ? "border-purple-400/70 bg-purple-500/20 text-white"
                    : "border-white/12 bg-white/[0.04] text-white/80 hover:border-white/30"
            }`}
        >
            {children}
        </button>
    );

    const Explain = ({ children }) => (
        <p className="mt-1 text-sm text-white/55">{children}</p>
    );

    const Screen = ({ tid, title, hint, children }) => (
        <div data-testid={tid} className="flex flex-col gap-3">
            <div>
                <h3 className="rf-heading text-xl font-bold text-white">{title}</h3>
                {hint ? <Explain>{hint}</Explain> : null}
            </div>
            <div className="flex flex-col gap-2">{children}</div>
        </div>
    );

    return (
        <div
            data-testid="wizard-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onKeyDown={(e) => { if (e.key === "Escape") onCancel?.(); containFocus(e); }}
        >
            <div ref={panelRef} tabIndex={-1} className="relative w-full max-w-md rounded-lg border border-white/12 bg-surface-raised p-5 outline-none sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                    <button
                        type="button"
                        data-testid="wizard-back"
                        onClick={back}
                        className="inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
                    >
                        <ChevronLeft className="h-4 w-4" /> Back
                    </button>
                    <span className="text-xs uppercase tracking-wide text-white/40">
                        {mode === "edit" ? "Edit template" : "Build your template"}
                    </span>
                    <button
                        type="button"
                        data-testid="wizard-close"
                        onClick={() => onCancel?.()}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {phase === "broll" && (
                    <Screen tid="wizard-step-broll" title="Add automatic B-roll?"
                        hint="We drop in relevant stock clips over your talking-head footage.">
                        <Opt tid="wizard-broll-yes" active={brollOn} onClick={() => { setBrollOn(true); advanceFrom("broll", { brollOn: true }); }}>Yes, add B-roll</Opt>
                        <Opt tid="wizard-broll-no" active={!brollOn} onClick={() => { setBrollOn(false); advanceFrom("broll", { brollOn: false }); }}>No, clean cut only</Opt>
                    </Screen>
                )}

                {phase === "broll_pct" && (
                    <Screen tid="wizard-step-broll_pct" title="How much B-roll?"
                        hint="Higher means more frequent cutaways.">
                        {[25, 50, 75].map((v) => (
                            <Opt key={v} tid={`wizard-broll-pct-${v}`} active={brollPct === v}
                                onClick={() => { setBrollPct(v); advanceFrom("broll_pct"); }}>{v}%</Opt>
                        ))}
                    </Screen>
                )}

                {phase === "silence" && (
                    <Screen tid="wizard-step-silence" title="Remove silences & dead air?"
                        hint="Tightens pacing by cutting long pauses and bad takes.">
                        <Opt tid="wizard-silence-yes" active={removeSilence} onClick={() => { setRemoveSilence(true); advanceFrom("silence", { removeSilence: true }); }}>Yes, remove silences</Opt>
                        <Opt tid="wizard-silence-no" active={!removeSilence} onClick={() => { setRemoveSilence(false); advanceFrom("silence", { removeSilence: false }); }}>No, keep every pause</Opt>
                    </Screen>
                )}

                {phase === "pace" && (
                    <Screen tid="wizard-step-pace" title="How aggressive should cuts be?"
                        hint="Faster trims more aggressively.">
                        {["natural", "fast", "extra-fast"].map((v) => (
                            <Opt key={v} tid={`wizard-pace-${v}`} active={silencePace === v}
                                onClick={() => { setSilencePace(v); advanceFrom("pace"); }}>{v.replace("-", " ")}</Opt>
                        ))}
                    </Screen>
                )}

                {phase === "caption" && (
                    <Screen tid="wizard-step-caption" title="Pick a caption style"
                        hint="Tap a style — the sample shows how it looks at export.">
                        <CaptionStyleGrid
                            styles={captionOptions}
                            value={caption}
                            onSelect={(tpl) => { setCaption(tpl); advanceFrom("caption"); }}
                            testIdPrefix="wizard-caption-opt"
                        />
                    </Screen>
                )}

                {phase === "music" && (
                    <Screen tid="wizard-step-music" title="Add background music?"
                        hint="Choose a track from your library.">
                        <Opt tid="wizard-music-yes" active={musicEnabled} onClick={() => { setMusicEnabled(true); advanceFrom("music", { musicEnabled: true }); }}>Yes, add music</Opt>
                        <Opt tid="wizard-music-no" active={!musicEnabled} onClick={() => { setMusicEnabled(false); advanceFrom("music", { musicEnabled: false }); }}>No music</Opt>
                    </Screen>
                )}

                {phase === "track" && (
                    <Screen tid="wizard-step-track" title="Choose a track"
                        hint="Preview, then pick one — previewing never selects.">
                        {musicError ? (
                            <div>
                                <p data-testid="wizard-track-error" className="text-sm text-amber-200/90">
                                    Your music library couldn't load.
                                </p>
                                <button type="button" data-testid="wizard-track-retry"
                                    onClick={() => onRequestMusicLoad?.()}
                                    className="mt-2 inline-flex min-h-11 items-center rounded-full border border-white/15 px-4 text-sm text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60">Retry</button>
                            </div>
                        ) : !musicLoadedOnce ? (
                            <p data-testid="wizard-track-loading" className="inline-flex items-center gap-2 text-sm text-white/60">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading music…
                            </p>
                        ) : musicTracks.length === 0 ? (
                            <p data-testid="wizard-track-empty" className="text-sm text-white/60">
                                No tracks in your library yet.
                            </p>
                        ) : (
                            <MusicTrackList
                                tracks={musicTracks}
                                selectedId={trackId}
                                onSelect={(id) => { setTrackId(id); advanceFrom("track"); }}
                                listTestId="wizard-track-list"
                                rowTestIdPrefix="wizard-track-opt"
                                previewTestIdPrefix="wizard-track-preview"
                                loadMoreTestId="wizard-track-load-more"
                                ariaLabel="Music track"
                            />
                        )}
                    </Screen>
                )}

                {phase === "name" && (
                    <Screen tid="wizard-step-name" title="Name your template"
                        hint="You'll reuse this on every upload.">
                        <input data-testid="wizard-name-input" autoFocus value={name} maxLength={60}
                            placeholder="e.g. My Shorts Recipe" disabled={saving}
                            onChange={(e) => { setName(e.target.value); setNameErr(""); setRetryPresetId(null); }}
                            onKeyDown={(e) => { if (e.key === "Enter" && !collision) submitName(); }}
                            className="h-11 w-full rounded-full border border-white/15 bg-black/50 px-4 text-sm text-white outline-none focus:border-purple-400/60" />
                        {nameErr && <p data-testid="wizard-name-error" className="text-sm text-amber-200/90">{nameErr}</p>}
                        {collision ? (
                            <>
                                <p data-testid="wizard-name-collision" className="text-sm text-amber-200/90">
                                    A template named “{name.trim()}” exists. Overwrite it, or choose a different name.
                                </p>
                                <button type="button" data-testid="wizard-name-overwrite" disabled={saving}
                                    onClick={() => doFinish(collision.id)}
                                    className="inline-flex min-h-11 items-center justify-center rounded-full border border-purple-400/50 bg-purple-500/20 px-4 text-sm font-semibold text-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:opacity-50">
                                    {saving ? "Saving…" : `Overwrite “${name.trim()}”`}
                                </button>
                            </>
                        ) : (
                            <button type="button" data-testid="wizard-name-save" disabled={saving || !name.trim()}
                                onClick={submitName}
                                className="inline-flex min-h-11 items-center justify-center rounded-full border border-purple-400/50 bg-purple-500/20 px-4 text-sm font-semibold text-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:opacity-50">
                                {saving ? "Saving…" : "Save & continue"}
                            </button>
                        )}
                        {retryPresetId && (
                            <button type="button" data-testid="wizard-default-retry" disabled={saving}
                                onClick={async () => { setSaving(true); try { await onRetryDefault?.(retryPresetId); } finally { setSaving(false); } }}
                                className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/20 px-4 text-sm text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60">
                                Retry setting default
                            </button>
                        )}
                    </Screen>
                )}
            </div>
        </div>
    );
};

export default FirstRunTemplateWizard;
